import Layout from '@/components/Layout'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { toast } from '@/hooks/use-toast'
import {
  ResultadoValidacaoUpload,
  useValidarUploadMutation
} from '@/hooks/useAssinaturas'
import { AlertCircle, CheckCircle2, FileText, Loader2, Upload } from 'lucide-react'
import { useCallback, useState } from 'react'

const MAX_SIZE_MB = 10
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024

function formatarData(s: string | undefined): string {
  if (!s) return '—'
  try {
    const d = new Date(s)
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return s
  }
}

const TIPO_LABEL: Record<string, string> = {
  triagem: 'Triagem',
  consulta_oftalmologica: 'Consulta Oftalmológica',
  prontuario_geralista: 'Prontuário Geralista',
  prontuario_retinologo: 'Prontuário Retinólogo',
  avaliacao_cardiologica: 'Avaliação Cardiológica',
  anamnese_pre_operatoria: 'Anamnese Pré-Operatória',
  encaminhamento_cirurgico: 'Encaminhamento Cirúrgico'
}

function ValidarDocumentoUploadCard({
  onResult
}: {
  onResult: (result: ResultadoValidacaoUpload) => void
}) {
  const [dragOver, setDragOver] = useState(false)
  const uploadMutation = useValidarUploadMutation()

  const validateAndSend = useCallback(
    async (file: File) => {
      if (file.type !== 'application/pdf') {
        toast({
          title: 'Arquivo inválido',
          description: 'Apenas arquivos PDF são aceitos.',
          variant: 'destructive'
        })
        return
      }
      if (file.size > MAX_SIZE_BYTES) {
        toast({
          title: 'Arquivo grande demais',
          description: `Tamanho máximo: ${MAX_SIZE_MB}MB.`,
          variant: 'destructive'
        })
        return
      }
      try {
        const result = await uploadMutation.mutateAsync(file)
        onResult(result)
      } catch (err: any) {
        toast({
          title: 'Erro ao validar',
          description: err?.message ?? 'Não foi possível validar o documento.',
          variant: 'destructive'
        })
      }
    },
    [onResult, uploadMutation]
  )

  const handleFile = (file: File | null) => {
    if (!file) return
    validateAndSend(file)
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    handleFile(file ?? null)
  }

  const onFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    handleFile(file ?? null)
    e.target.value = ''
  }

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold mb-2">Enviar PDF para validar</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Arraste um arquivo PDF aqui ou clique para selecionar (máx. {MAX_SIZE_MB}MB).
      </p>
      <div
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-colors
          ${dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'}
        `}
      >
        <input
          type="file"
          accept="application/pdf"
          onChange={onFileInput}
          className="hidden"
          id="validar-doc-upload"
        />
        <label htmlFor="validar-doc-upload" className="cursor-pointer block">
          {uploadMutation.isPending ? (
            <Loader2 className="h-12 w-12 mx-auto animate-spin text-muted-foreground" />
          ) : (
            <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
          )}
          <span className="mt-2 block text-sm text-muted-foreground">
            {uploadMutation.isPending ? 'Validando...' : 'Selecionar PDF'}
          </span>
        </label>
      </div>
    </Card>
  )
}

function ResultadoValidacaoCard({ result }: { result: ResultadoValidacaoUpload | null }) {
  if (!result) return null

  if (result.status === 'NÃO_ENCONTRADO') {
    return (
      <Card className="p-6 border-warning/50">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-10 w-10 text-warning shrink-0" />
          <div>
            <h3 className="font-semibold text-warning">Documento não encontrado</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Este PDF não corresponde a nenhum documento assinado digitalmente no sistema.
              Verifique se o arquivo é uma cópia fiel do documento original assinado.
            </p>
          </div>
        </div>
      </Card>
    )
  }

  if (result.status === 'VÁLIDO') {
    return (
      <Card className="p-6 border-success/50">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-10 w-10 text-success shrink-0" />
          <div className="space-y-1">
            <h3 className="font-semibold text-success">Documento válido</h3>
            {result.empresa && (
              <p className="text-sm">
                <span className="text-muted-foreground">Empresa: </span>
                {result.empresa.nome ?? '—'}
                {result.empresa.cnpj && ` (CNPJ: ${result.empresa.cnpj})`}
              </p>
            )}
            {result.medico && (
              <p className="text-sm">
                <span className="text-muted-foreground">Assinante: </span>
                {result.medico}
              </p>
            )}
            {result.dataAssinatura && (
              <p className="text-sm">
                <span className="text-muted-foreground">Data da assinatura: </span>
                {formatarData(result.dataAssinatura)}
              </p>
            )}
            {result.tipoDocumento && (
              <p className="text-sm">
                <span className="text-muted-foreground">Tipo: </span>
                {TIPO_LABEL[result.tipoDocumento] ?? result.tipoDocumento}
              </p>
            )}
          </div>
        </div>
      </Card>
    )
  }

  return (
    <Card className="p-6 border-destructive/50">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-10 w-10 text-destructive shrink-0" />
        <div>
          <h3 className="font-semibold text-destructive">Documento inválido</h3>
          <p className="text-sm text-muted-foreground mt-1">
            A assinatura deste documento foi invalidada (conteúdo alterado após a assinatura).
          </p>
        </div>
      </div>
    </Card>
  )
}

export default function ValidarDocumentoPage() {
  const [resultado, setResultado] = useState<ResultadoValidacaoUpload | null>(null)

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <FileText className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Validar documentos</h1>
            <p className="text-muted-foreground">
              Envie um PDF assinado digitalmente para verificar sua autenticidade.
            </p>
          </div>
        </div>

        <ValidarDocumentoUploadCard onResult={setResultado} />
        <ResultadoValidacaoCard result={resultado} />
      </div>
    </Layout>
  )
}
